package com.arena_web.ui.layouts

import androidx.compose.runtime.*
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.BackToTopButton
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.arena_web.ui.sections.header.AppHeader
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.*
import com.arena_web.utils.AppSettings
import com.arena_web.utils.PageSeo
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier

import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*

/**
 * Shared page layout for all app pages (Arena, Profile, Chat, Events).
 * Static header (125px total: 30px top padding + 63px header + 32px gap),
 * dynamic content fills remaining space.
 */
@Composable
fun AppPageLayout(
    activeTab: SidebarTab? = null,
    // SEO: sets the document title ("Arena Pro Play - <title>") and per-page
    // meta description — these land in the static export, so public pages
    // should always pass them.
    title: String? = null,
    description: String = "",
    onSportChanged: (() -> Unit)? = null,
    backgroundImage: String? = null,
    mobileReady: Boolean = false,
    // Mobile-only: when set, replaces the sport-selector pill in the top bar
    // with the page title (Figma 8285:172272).
    mobilePageTitle: String? = null,
    // Mobile-only: chooses the right-side header action button (bell vs
    // heart) so per-page chrome can swap notifications for likes.
    mobileHeaderAction: com.arena_web.ui.sections.header.MobileHeaderAction =
        com.arena_web.ui.sections.header.MobileHeaderAction.Notification,
    // Mobile-only: hide the bottom nav for full-screen subviews.
    mobileHideBottomNav: Boolean = false,
    // Mobile-only: hide the global top bar for full-screen subviews.
    mobileHideTopBar: Boolean = false,
    // Mobile-only: drop the 16-px horizontal safe-area gutter so child content
    // can paint edge-to-edge (e.g. a full-bleed hero banner). The child then
    // owns its own horizontal padding where it needs it.
    mobileEdgeToEdge: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    if (title != null) {
        PageSeo(title = "Arena Pro Play - $title", description = description)
    }

    // Ensure role is synced when entering player pages
    LaunchedEffect(Unit) {
        Storage.save(StorageKeys.ACTIVE_ROLE, "PLAYER")
    }

    // Reactive sport — reads from localStorage, updates when header changes sport
    var currentSport by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all")
    }
    val effectiveSport = AppSettings.globalPalette ?: currentSport

    val mobile = isMobile()

    CompositionLocalProvider(LocalSportColors provides SportColors.forSport(effectiveSport)) {
        if (mobile) {
            MobilePageLayout(
                activeTab = activeTab,
                onSportChanged = {
                    currentSport = (Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all")
                    onSportChanged?.invoke()
                },
                mobileReady = mobileReady,
                pageTitle = mobilePageTitle,
                headerAction = mobileHeaderAction,
                hideBottomNav = mobileHideBottomNav,
                hideTopBar = mobileHideTopBar,
                edgeToEdge = mobileEdgeToEdge,
                content = content
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(100.vh)
                    .overflow(Overflow.Hidden)
                    .then(
                        if (backgroundImage != null) Modifier.styleModifier {
                            property("background-image", "url('$backgroundImage')")
                            property("background-size", "cover")
                            property("background-position", "center")
                            property("background-repeat", "no-repeat")
                        } else Modifier
                    )
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                ) {
                    Box(modifier = Modifier.fillMaxWidth().flexShrink(0).sPadding(30, 40).sPaddingBottom(0)) {
                        AppHeader(
                            activeTab = activeTab,
                            onSportChanged = { sportName ->
                                currentSport = sportName.lowercase()
                                onSportChanged?.invoke()
                            }
                        )
                    }

                    // Dynamic content — fills remaining height
                    Column(
                        modifier = Modifier.fillMaxWidth().flexGrow(1)
                            .minHeight(0.px)
                    ) {
                        content()
                    }
                }
            }
        }
    }
}

@Composable
fun PageLayout(
    title: String = "Arena Pro Play",
    // Full <title> override — used by the home page to serve a keyword-rich
    // SEO title instead of the "Arena Pro Play - <page>" tab pattern.
    seoTitle: String? = null,
    description: String = "",
    ogImage: String = "/og-image.png",
    // Opt-in: phones get the app-style MobilePageLayout (top bar + bottom
    // nav) instead of the scaled-down marketing desktop layout. Pages that
    // don't opt in keep their previous mobile rendering.
    mobileReady: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    PageSeo(
        title = seoTitle ?: "Arena Pro Play - $title",
        description = description,
        ogImage = ogImage
    )

    if (mobileReady && isMobile()) {
        val sport = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
        CompositionLocalProvider(
            LocalSportColors provides SportColors.forSport(AppSettings.globalPalette ?: sport)
        ) {
            MobilePageLayout(mobileReady = true, content = content)
        }
        return
    }

    Box(
        Modifier
            .fillMaxWidth()
            .height(100.vh)
            .overflow(Overflow.Hidden)
            .backgroundColor(colors.background)
    ) {
        Column(
            Modifier
                .fillMaxSize()
                .sPadding(30, 40)
                .sPaddingBottom(0),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Shared header on all pages — fixed 63px height, do not let flex shrink it.
            // Positioned above the home page's fixed ambient-orb layer (z 0) and the
            // content column (z 1) so the glow never washes over header controls.
            Box(
                modifier = Modifier.fillMaxWidth().flexShrink(0)
                    .styleModifier {
                        property("position", "relative")
                        property("z-index", "2")
                    }
            ) {
                AppHeader()
            }

            Column(
                HiddenScrollbarStyle.toModifier()
                    .fillMaxWidth().flexGrow(1).minHeight(0.px)
                    .styleModifier { property("overflow-y", "auto") },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                content()
            }

            BackToTopButton()
        }
    }
}
